(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.ElephoneMotion = window.ElephoneMotion || {};
  window.ElephoneMotion.reducedMotion = reducedMotion;
  document.documentElement.classList.toggle('reduced-motion', reducedMotion);

  if (!window.gsap || !window.ScrollTrigger) {
    /* GSAP/ScrollTrigger failed to load (CDN blocked, network error, etc).
       .js-enabled only exists to scope the [data-reveal] hidden CSS state —
       without GSAP nothing will ever reveal it, so drop the class and let
       every reveal-gated element render at its natural, always-visible state. */
    document.documentElement.classList.remove('js-enabled');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  function killScrollTriggers() {
    if (window.ScrollTrigger) {
      ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
    }
  }

  function initReveals() {
    var reduced = window.ElephoneMotion.reducedMotion;
    var durationIn = reduced ? 0.3 : 0.8;
    var durationOut = reduced ? 0.3 : 0.4;
    var easeIn = reduced ? 'power1.out' : 'power2.out';
    var yFrom = reduced ? 0 : 28;

    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      var children = el.querySelectorAll('[data-reveal-child]');
      var targets = children.length ? children : el;
      /* Above-the-fold elements (e.g. trust badges) opt out of the
         bidirectional replay and keep the old play-once behavior. */
      var once = el.hasAttribute('data-reveal-once');

      if (children.length) {
        /* Parent stays visible — only the children animate */
        gsap.set(el, { opacity: 1, y: 0 });
      }
      gsap.set(targets, { opacity: 0, y: yFrom });

      if (once) {
        gsap.to(targets, {
          opacity: 1,
          y: 0,
          duration: durationIn,
          ease: easeIn,
          stagger: children.length ? 0.12 : 0,
          scrollTrigger: { trigger: el, start: 'top 85%', once: true }
        });
        return;
      }

      /* Bidirectional: plays forward on enter, reverses (faster, via
         timeScale) when it scrolls back out the bottom on the way up —
         so scrolling up never feels like content went missing. */
      var tween = gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: durationIn,
        ease: easeIn,
        stagger: children.length ? 0.12 : 0,
        paused: true,
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          end: 'bottom top',
          onEnter: function () { tween.timeScale(1).play(); },
          onLeaveBack: function () { tween.timeScale(durationIn / durationOut).reverse(); }
        }
      });
    });
  }

  var floatTweens = [];
  var floatObservers = [];

  function killFloats() {
    floatTweens.forEach(function (t) { t.kill(); });
    floatTweens = [];
    floatObservers.forEach(function (o) { o.disconnect(); });
    floatObservers = [];
  }

  function initFloats() {
    killFloats();

    /* Decorative-only, infinite loop — skip entirely under reduced motion */
    if (window.ElephoneMotion.reducedMotion) return;

    document.querySelectorAll('[data-float]').forEach(function (el) {
      var duration = 4 + Math.random() * 2; /* 4–6s */
      var rotateAmount = el.hasAttribute('data-float-rotate') ? 1.5 : 0;
      var delay = -(Math.random() * duration); /* desync — never move in sync */

      var tween = gsap.fromTo(
        el,
        { y: -8, rotation: -rotateAmount },
        {
          y: 8,
          rotation: rotateAmount,
          duration: duration,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: delay
        }
      );
      floatTweens.push(tween);

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              tween.play();
            } else {
              tween.pause();
            }
          });
        }, { threshold: 0 });
        io.observe(el);
        floatObservers.push(io);
      }
    });
  }

  var parallaxMM = null;

  function killParallax() {
    if (parallaxMM) {
      parallaxMM.revert();
      parallaxMM = null;
    }
  }

  function initParallax() {
    killParallax();

    if (window.ElephoneMotion.reducedMotion) return;

    var DRIFT_SCALE = 200; /* px per unit of (factor - 1) */
    var MAX_DRIFT = 30;    /* px each direction — 60px total peak-to-peak ceiling */

    parallaxMM = ScrollTrigger.matchMedia({
      '(min-width: 768px)': function () {
        document.querySelectorAll('[data-parallax]').forEach(function (el) {
          var factor = parseFloat(el.getAttribute('data-parallax')) || 1;
          var drift = (factor - 1) * DRIFT_SCALE;
          drift = Math.max(-MAX_DRIFT, Math.min(MAX_DRIFT, drift));

          gsap.fromTo(
            el,
            { y: -drift },
            {
              y: drift,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8
              }
            }
          );
        });
      }
    });
  }

  var driftMM = null;

  function killDrifts() {
    if (driftMM) {
      driftMM.revert();
      driftMM = null;
    }
  }

  /* General rule: a [data-drift] element must live inside a wrapper with
     enough breathing room (padding-block >= drift value) OR overflow
     visible up the DOM chain — otherwise the ancestor clips the glyphs
     as the element translates past the box edge. This walks up from the
     element and warns in the console if it finds a clipping ancestor,
     so regressions surface immediately instead of silently slicing text. */
  function auditDriftOverflow(el, px) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var cs = getComputedStyle(node);
      if (cs.overflow === 'hidden' || cs.overflowY === 'hidden' ||
          cs.overflow === 'clip' || cs.overflowY === 'clip') {
        console.warn(
          '[scroll-animations] [data-drift="' + px + '"] element may be clipped by an ancestor with overflow hidden/clip — ' +
          'give it overflow: visible or padding-block >= ' + Math.abs(px) + 'px.',
          node, el
        );
        return;
      }
      node = node.parentElement;
    }
  }

  function initDrifts() {
    killDrifts();

    if (window.ElephoneMotion.reducedMotion) return;

    driftMM = ScrollTrigger.matchMedia({
      '(min-width: 768px)': function () {
        document.querySelectorAll('[data-drift]').forEach(function (el) {
          var px = parseFloat(el.getAttribute('data-drift'));
          if (!px) return;

          auditDriftOverflow(el, px);

          gsap.fromTo(
            el,
            { y: px },
            {
              y: -px,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1
              }
            }
          );
        });
      }
    });
  }

  function init() {
    killScrollTriggers();
    initReveals();
    initFloats();
    initParallax();
    initDrifts();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);

  window.ElephoneMotion.init = init;
})();
